<script setup lang="ts">
/**
 * Footer — odwzorowanie base.html footer z Django.
 * Avatar Liira z animowanym obramowaniem, link do admina (staff) lub strony autora.
 * Tutorial, Support, Other Links toggle.
 */
import { useAuthStore } from '@/stores/auth'
import { useLobbyStore, type FooterLink } from '@/stores/lobby'
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'

const auth = useAuthStore()
const lobby = useLobbyStore()
const route = useRoute()
let realtimeRestartTimer: ReturnType<typeof setTimeout> | null = null
let footerFallbackTimer: ReturnType<typeof setInterval> | null = null
const footerLinks = computed(() => lobby.serverInfo?.footer_links || [])
const footerLanguage = computed<'pl' | 'en'>(() => {
  const languages = [...(navigator.languages || []), navigator.language]
  return languages.some(language => String(language).toLowerCase().startsWith('pl')) ? 'pl' : 'en'
})

function footerLinkName(link: FooterLink): string {
  return footerLanguage.value === 'pl'
    ? (link.name_pl || link.name_eng || link.name)
    : (link.name_eng || link.name_pl || link.name)
}

async function refreshFooterRealtime(): Promise<void> {
  await lobby.fetchServerInfo()
  if (auth.isAuthenticated) lobby.startLobbyRealtime()
}

function scheduleFooterRealtimeRefresh(): void {
  if (realtimeRestartTimer) clearTimeout(realtimeRestartTimer)
  realtimeRestartTimer = setTimeout(() => {
    realtimeRestartTimer = null
    void refreshFooterRealtime()
  }, 0)
}

onMounted(() => {
  void refreshFooterRealtime()
  footerFallbackTimer = setInterval(() => void lobby.fetchServerInfo(), 30_000)
})
onUnmounted(() => {
  if (realtimeRestartTimer) clearTimeout(realtimeRestartTimer)
  if (footerFallbackTimer) clearInterval(footerFallbackTimer)
  lobby.stopLobbyRealtime()
})
watch(() => route.fullPath, scheduleFooterRealtimeRefresh)
</script>

<template>
  <footer
    class="p-1 w-full pt-1 text-gray-200 flex justify-center flex-wrap stroked"
  >
    <!-- Liir signature — avatar + sigbox -->
    <div class="sigposition">
      <component
        :is="auth.isStaff ? 'RouterLink' : 'a'"
        v-bind="auth.isStaff
          ? { to: '/admin' }
          : { href: 'http://liir.myartsonline.com/', target: '_blank', rel: 'noopener noreferrer' }"
        class="no-underline"
      >
        <div class="avatar-container">
          <figure class="profile-img">
            <div class="animated-border-box-glow" />
            <div class="animated-border-box" />
          </figure>
        </div>
        <div class="sigbox">
          <div class="signature">
            <h2>Liir- service owner</h2>
          </div>
        </div>
      </component>
    </div>

    <!-- Footer links -->
    <template v-for="link in footerLinks" :key="link.id">
      <RouterLink
        v-if="link.system_key"
        :to="link.url"
        :title="link.title || footerLinkName(link)"
        class="px-4 futi support-link no-underline"
      >
        {{ footerLinkName(link) }}
      </RouterLink>
      <a
        v-else
        :href="link.url"
        :title="link.title || footerLinkName(link)"
        target="_blank"
        class="px-4 futi support-link no-underline"
        rel="noopener noreferrer"
      >
        {{ footerLinkName(link) }}
      </a>
    </template>

  </footer>
</template>
